/**
 * Postmoro — Email Builder (Visual UX Overhaul, Phase 2 Ticket 2.5)
 *
 * Design intent:
 *   - Left rail:   compact visual content palette (icon cards)
 *   - Center:      REAL email canvas (white document on soft-gray editing
 *                  environment) with inline editing, hover controls,
 *                  insertion affordances, empty-state, device toggle,
 *                  zoom.
 *   - Right rail:  contextual (block Content/Style/Actions when a block
 *                  is selected, or global Email Design when nothing is).
 *   - Top bar:     Campaign name / Subject / Preheader + toolbar
 *                  (Templates / Create with AI / Preview / Save).
 *
 * WYSIWYG rule (Ticket 2.5): every visual control here maps 1:1 to a
 * field the backend renderer already honours. If a control is not yet
 * schema-backed, it is deferred — never fake persistence.
 *
 * Personalization (from Ticket 2): merge tags are stored as canonical
 * `{{tag}}` literals in the schema. In inline-edit UI we render them as
 * pill "chips" for readability, but never rewrite the schema. Preview
 * (with sample recipient) still runs the backend resolver.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import GlassCard from "../components/GlassCard";
import {
  Type, AlignLeft, Image as ImageIcon, MousePointerClick, Minus,
  ArrowUpDown, ArrowUp, ArrowDown, Copy, Trash2, Wand2, LayoutTemplate,
  Eye, Save, Loader2, Sparkles, Info, User, Plus, Monitor, Smartphone,
  ZoomIn, ZoomOut, Palette, Settings2, GripVertical,
  AlignCenter, AlignRight, Bold, Layers, Columns, Share2, Video, Package,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BLOCK_LIBRARY = [
  { type: "heading",  label: "Heading",  icon: Type,               seed: { content: { text: "Your headline goes here" }, settings: { level: 1, align: "left" } } },
  { type: "text",     label: "Text",     icon: AlignLeft,          seed: { content: { text: "Write a short paragraph that gives readers a reason to keep reading." }, settings: { align: "left" } } },
  { type: "image",    label: "Image",    icon: ImageIcon,          seed: { content: { src: "", alt: "" }, settings: {} } },
  { type: "button",   label: "Button",   icon: MousePointerClick,  seed: { content: { text: "Learn more", url: "" }, settings: { align: "center" } } },
  { type: "divider",  label: "Divider",  icon: Minus,              seed: { content: {}, settings: {} } },
  { type: "spacer",   label: "Spacer",   icon: ArrowUpDown,        seed: { content: {}, settings: { height: 24 } } },
];

// Future / not-yet-wired visual placeholders (rule 6). Clicking them
// shows a friendly toast rather than adding to the schema.
const FUTURE_BLOCKS = [
  { type: "columns",  label: "Columns", icon: Columns },
  { type: "social",   label: "Social",  icon: Share2 },
  { type: "logo",     label: "Logo",    icon: Layers },
  { type: "video",    label: "Video",   icon: Video },
  { type: "product",  label: "Product", icon: Package },
];

const MERGE_TAG_ALLOWLIST = [
  { name: "first_name",    label: "First name",    sample: "Sarah" },
  { name: "last_name",     label: "Last name",     sample: "Johnson" },
  { name: "email",         label: "Email address", sample: "sarah@example.com" },
  { name: "business_name", label: "Business name", sample: "Example Business" },
];
const MERGE_LABEL_BY_NAME = Object.fromEntries(MERGE_TAG_ALLOWLIST.map((t) => [t.name, t.label]));
const MERGE_TAG_RE = /\{\{\s*([a-z_][a-z0-9_]{0,31})(?:\s*\|\s*[^{}|]{0,80})?\s*\}\}/gi;

// Email-safe font families (approved for cross-client rendering).
const FONT_CHOICES = [
  { label: "System (Helvetica)", value: "Helvetica, Arial, sans-serif" },
  { label: "Arial",              value: "Arial, Helvetica, sans-serif" },
  { label: "Georgia",            value: "Georgia, 'Times New Roman', serif" },
  { label: "Times New Roman",    value: "'Times New Roman', Times, serif" },
  { label: "Verdana",            value: "Verdana, Geneva, sans-serif" },
  { label: "Trebuchet MS",       value: "'Trebuchet MS', 'Lucida Sans Unicode', sans-serif" },
];

const AI_GOAL_CHOICES = [
  "Promote an offer", "Announce something", "Drive website traffic",
  "Promote a product or service", "Invite people to an event",
  "Re-engage customers", "Share an update", "Welcome new subscribers",
];
const AI_AUDIENCE_CHOICES = [
  "All subscribers", "Existing customers", "New leads", "Past customers",
  "VIP customers", "New subscribers", "Local customers",
];
const AI_TONE_CHOICES = [
  "Match my brand", "Professional", "Friendly", "Bold", "Conversational",
  "Luxury", "Playful", "Urgent", "Educational", "Warm",
];
const AI_CTA_CHOICES = [
  "Shop now", "Learn more", "Book now", "Get started", "Claim offer",
  "Visit website", "Register now", "Contact us", "Read more",
];

const ZOOM_STEPS = [0.75, 1.0, 1.25];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const uid = () => `b_${Math.random().toString(36).slice(2, 10)}`;

function insertAtSelection(el, snippet) {
  if (!el) return null;
  const start = el.selectionStart ?? el.value.length;
  const end   = el.selectionEnd   ?? el.value.length;
  const before = el.value.slice(0, start);
  const after  = el.value.slice(end);
  const next   = `${before}${snippet}${after}`;
  const caret  = (before + snippet).length;
  return { next, caret };
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

/** Render text with `{{tag}}` occurrences as inline "chips". Non-tag text
 *  is HTML-escaped. Returns a React node array. */
function renderWithChips(text) {
  const src = String(text ?? "");
  const nodes = [];
  let last = 0;
  const re = new RegExp(MERGE_TAG_RE.source, "gi");
  let m;
  let key = 0;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) nodes.push(<React.Fragment key={key++}>{src.slice(last, m.index)}</React.Fragment>);
    const name = (m[1] || "").toLowerCase();
    const label = MERGE_LABEL_BY_NAME[name] || name;
    nodes.push(
      <span key={key++}
        contentEditable={false}
        className="inline-flex items-center gap-1 px-1.5 py-[1px] mx-[1px] rounded-md text-[11px] font-medium bg-emerald-100 text-emerald-900 border border-emerald-300 align-middle select-none"
        title={`Merge tag: ${m[0]}`}
        data-merge-tag={name}>
        <User className="h-2.5 w-2.5" /> {label}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < src.length) nodes.push(<React.Fragment key={key++}>{src.slice(last)}</React.Fragment>);
  return nodes.length ? nodes : [<React.Fragment key="empty" />];
}

// ---------------------------------------------------------------------------
// MergeTagInserter — pill-picker popover for merge tags
// ---------------------------------------------------------------------------

function MergeTagInserter({ inputRef, onInsert, testid, size = "sm", label = "Personalize" }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} aria-label="Insert merge tag"
          className={`inline-flex items-center gap-1 rounded-md border border-white/15 hover:bg-white/5 ${size === "sm" ? "px-2 py-1 text-[11px]" : "px-2.5 py-1 text-xs"} opacity-90 focus:outline-none focus:ring-2 focus:ring-emerald-400`}>
          <User className="h-3 w-3" /> {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" data-testid={`${testid}-popover`}>
        <div className="text-[11px] uppercase tracking-wide opacity-60 px-1 pb-1">Insert merge tag</div>
        {MERGE_TAG_ALLOWLIST.map((t) => (
          <button key={t.name} type="button" data-testid={`${testid}-tag-${t.name}`}
            onClick={() => {
              const snippet = `{{${t.name}}}`;
              const el = inputRef?.current;
              const res = insertAtSelection(el, snippet);
              onInsert(res ? res.next : (el ? el.value + snippet : snippet), res?.caret);
              setOpen(false);
              setTimeout(() => { if (el && res) { el.focus(); el.setSelectionRange(res.caret, res.caret); } }, 0);
            }}
            className="w-full text-left px-2 py-1.5 rounded hover:bg-white/5 text-xs flex items-center justify-between">
            <span>{t.label}</span>
            <code className="opacity-60">{`{{${t.name}}}`}</code>
          </button>
        ))}
        <div className="mt-2 border-t border-white/10 pt-2 text-[10px] opacity-60 px-1">
          Renders per recipient at send time. Unsupported tags are ignored.
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Left rail — Visual Content Palette
// ---------------------------------------------------------------------------

function PaletteCard({ icon: Icon, label, testid, onClick, disabled = false, title }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      data-testid={testid} title={title || label} aria-label={label}
      className={`group flex flex-col items-center justify-center gap-1 rounded-lg border transition p-2 min-h-[62px]
        ${disabled
          ? "border-white/5 opacity-40 cursor-not-allowed"
          : "border-white/10 hover:border-emerald-400/60 hover:bg-white/5 active:scale-[.98]"}`}>
      <Icon className="h-5 w-5 opacity-90" />
      <div className="text-[11px] leading-none opacity-80">{label}</div>
    </button>
  );
}

function BlockPalette({ onAdd }) {
  return (
    <div className="space-y-4" data-testid="builder-palette">
      <div>
        <div className="text-[11px] uppercase tracking-wide opacity-60 mb-2">Content blocks</div>
        <div className="grid grid-cols-2 gap-2">
          {BLOCK_LIBRARY.map((b) => (
            <PaletteCard key={b.type} icon={b.icon} label={b.label}
              testid={`palette-add-${b.type}`} onClick={() => onAdd(b)} />
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide opacity-60 mb-2">Coming soon</div>
        <div className="grid grid-cols-2 gap-2">
          {FUTURE_BLOCKS.map((b) => (
            <PaletteCard key={b.type} icon={b.icon} label={b.label}
              testid={`palette-future-${b.type}`}
              onClick={() => toast.info(`${b.label} block is coming in a future update.`)}
              title={`${b.label} — coming soon`} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canvas — inline-editable, hover controls, insertion affordances
// ---------------------------------------------------------------------------

function HoverToolbar({ id, onMoveUp, onMoveDown, onDuplicate, onDelete, isFirst, isLast, visible }) {
  return (
    <div
      className={`absolute -top-3 right-2 flex items-center gap-1 bg-neutral-900 text-white rounded-md px-1 py-0.5 shadow transition-opacity ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`hover-toolbar-${id}`}>
      <button disabled={isFirst} onClick={onMoveUp} aria-label="Move up"  title="Move up"
        data-testid={`block-up-${id}`}       className="p-1 opacity-90 disabled:opacity-30 hover:text-emerald-300"><ArrowUp className="h-3 w-3" /></button>
      <button disabled={isLast}  onClick={onMoveDown} aria-label="Move down" title="Move down"
        data-testid={`block-down-${id}`}     className="p-1 opacity-90 disabled:opacity-30 hover:text-emerald-300"><ArrowDown className="h-3 w-3" /></button>
      <button onClick={onDuplicate} aria-label="Duplicate" title="Duplicate"
        data-testid={`block-duplicate-${id}`} className="p-1 opacity-90 hover:text-emerald-300"><Copy className="h-3 w-3" /></button>
      <button onClick={onDelete}    aria-label="Delete"    title="Delete"
        data-testid={`block-delete-${id}`}   className="p-1 text-red-400 hover:text-red-300"><Trash2 className="h-3 w-3" /></button>
    </div>
  );
}

function InsertBetween({ onAdd, testid }) {
  return (
    <div className="relative h-2 group" data-testid={testid}>
      <div className="absolute inset-x-4 top-1/2 h-px bg-transparent group-hover:bg-emerald-300/70 transition" />
      <button type="button" aria-label="Add block here" title="Add block here"
        onClick={onAdd}
        data-testid={`${testid}-btn`}
        className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none">
        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500 text-white shadow">
          <Plus className="h-3 w-3" />
        </span>
      </button>
    </div>
  );
}

/** ContentEditable field that renders `{{tag}}` occurrences as chips.
 *  Persists the CANONICAL string (with braces) — chips are visual only. */
function InlineEditable({ value, onChange, block, className, style, testid, ariaLabel, tag = "div" }) {
  const ref = useRef(null);
  const composing = useRef(false);
  // Build the DOM ourselves on every value change (unless user is
  // editing, then keep DOM as-is to avoid caret jumps).
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    if (dirty) return;
    // Clear + rebuild
    while (ref.current.firstChild) ref.current.removeChild(ref.current.firstChild);
    renderIntoDom(ref.current, value);
  }, [value, dirty]);

  function renderIntoDom(host, text) {
    const src = String(text ?? "");
    const re = new RegExp(MERGE_TAG_RE.source, "gi");
    let last = 0; let m;
    while ((m = re.exec(src)) !== null) {
      if (m.index > last) host.appendChild(document.createTextNode(src.slice(last, m.index)));
      const chip = document.createElement("span");
      chip.setAttribute("contenteditable", "false");
      chip.setAttribute("data-merge-tag", (m[1] || "").toLowerCase());
      chip.setAttribute("data-canonical", m[0]);
      chip.className = "inline-flex items-center gap-1 px-1.5 py-[1px] mx-[1px] rounded-md text-[11px] font-medium bg-emerald-100 text-emerald-900 border border-emerald-300 align-middle select-none";
      chip.title = `Merge tag: ${m[0]}`;
      // small user icon
      chip.textContent = MERGE_LABEL_BY_NAME[(m[1] || "").toLowerCase()] || m[1] || "tag";
      host.appendChild(chip);
      last = m.index + m[0].length;
    }
    if (last < src.length) host.appendChild(document.createTextNode(src.slice(last)));
  }

  const readBack = () => {
    const host = ref.current;
    if (!host) return "";
    // Walk children: chips -> data-canonical, else textContent.
    let out = "";
    host.childNodes.forEach((n) => {
      if (n.nodeType === 3) { out += n.textContent || ""; }
      else if (n.nodeType === 1 && n.getAttribute && n.getAttribute("data-canonical")) {
        out += n.getAttribute("data-canonical");
      } else if (n.nodeType === 1) {
        // Fallback: use text but strip other markup.
        out += n.textContent || "";
      }
    });
    return out;
  };

  const commit = () => {
    setDirty(false);
    const next = readBack();
    if (next !== value) onChange(next);
  };

  const Tag = tag;
  return (
    <Tag
      ref={ref}
      role="textbox"
      aria-label={ariaLabel}
      data-testid={testid}
      contentEditable
      suppressContentEditableWarning
      spellCheck={true}
      className={className + " outline-none focus:ring-2 focus:ring-emerald-400/60 rounded-[3px]"}
      style={style}
      onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={() => { composing.current = false; setDirty(true); }}
      onFocus={() => setDirty(true)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
      }}
      onInput={() => { /* keep dirty=true so React does not re-render */ }}
    />
  );
}

function BlockView({ block, selected, hovered, onSelect, onHover, onLeave, onChange, isFirst, isLast, onMoveUp, onMoveDown, onDuplicate, onDelete }) {
  const c = block.content || {}; const s = block.settings || {};
  const setC = (k, v) => onChange({ ...block, content: { ...c, [k]: v } });
  const outline = selected
    ? "ring-2 ring-emerald-400"
    : hovered ? "ring-1 ring-emerald-300/50" : "ring-1 ring-transparent";
  const wrap = (children) => (
    <div
      onMouseEnter={() => onHover(block.id)} onMouseLeave={onLeave}
      onClick={() => onSelect(block.id)}
      data-testid={`canvas-block-${block.id}`}
      data-block-type={block.type}
      className={`relative rounded-[2px] transition ${outline} ${selected || hovered ? "bg-emerald-50/30" : ""}`}>
      <div className="px-1 py-1">{children}</div>
      <HoverToolbar id={block.id}
        onMoveUp={(e) => { onMoveUp(); }} onMoveDown={(e) => { onMoveDown(); }}
        onDuplicate={() => onDuplicate()} onDelete={() => onDelete()}
        isFirst={isFirst} isLast={isLast}
        visible={selected || hovered} />
    </div>
  );

  if (block.type === "heading") {
    const level = s.level || 2;
    const size = { 1: "text-2xl", 2: "text-xl", 3: "text-lg" }[level] || "text-xl";
    return wrap(
      <InlineEditable value={c.text || ""} block={block}
        onChange={(v) => setC("text", v)}
        tag="div"
        testid={`inline-heading-${block.id}`} ariaLabel="Heading text"
        className={`${size} font-semibold min-h-[1.6em]`}
        style={{ color: s.color || "#111827", textAlign: s.align || "left" }}
      />,
    );
  }
  if (block.type === "text") {
    return wrap(
      <InlineEditable value={c.text || ""} block={block}
        onChange={(v) => setC("text", v)}
        tag="div"
        testid={`inline-text-${block.id}`} ariaLabel="Paragraph text"
        className="text-[15px] leading-[1.55] min-h-[1.6em]"
        style={{ color: s.color || "#374151", textAlign: s.align || "left" }}
      />,
    );
  }
  if (block.type === "image") {
    return wrap(
      c.src ? (
        <img src={c.src} alt={c.alt || ""}
          className="max-w-full h-auto block"
          style={{
            marginLeft: (s.align === "center") ? "auto" : (s.align === "right" ? "auto" : 0),
            marginRight: (s.align === "center") ? "auto" : (s.align === "left" ? "auto" : 0),
            maxWidth: (s.max_width ? `${s.max_width}px` : "100%"),
          }} />
      ) : (
        <div data-testid={`image-empty-${block.id}`}
          className="flex flex-col items-center justify-center gap-2 py-10 rounded border-2 border-dashed"
          style={{ color: "#6b7280", borderColor: "#d1d5db", background: "#f9fafb" }}>
          <ImageIcon className="h-8 w-8 opacity-70" />
          <div className="text-sm">Add an image</div>
          <div className="text-[11px] opacity-70">Use the right panel — Media Library or paste an HTTPS URL.</div>
        </div>
      ),
    );
  }
  if (block.type === "button") {
    return wrap(
      <div style={{ textAlign: s.align || "center" }}>
        <span
          data-testid={`button-visual-${block.id}`}
          className="inline-block px-5 py-2.5 rounded-md text-sm font-medium shadow-sm"
          style={{
            backgroundColor: s.bg_color || "#111827",
            color: s.color || "#ffffff",
          }}>
          {c.text || "Button"}
        </span>
      </div>,
    );
  }
  if (block.type === "divider") {
    return wrap(<hr style={{ borderTop: "1px solid #e5e7eb", borderBottom: 0, borderLeft: 0, borderRight: 0, margin: "8px 0" }} />);
  }
  if (block.type === "spacer") {
    const h = Math.max(4, Math.min(96, Number(s.height) || 24));
    return wrap(<div style={{ height: `${h}px`, background: "#f3f4f6", borderRadius: 4 }} />);
  }
  if (block.type === "footer" || block.type === "columns" || block.type === "social_links") {
    return wrap(<div className="text-xs italic border border-dashed rounded p-2" style={{ color: "#6b7280", borderColor: "#e5e7eb" }}>Advanced block: <code>{block.type}</code> (read-only in this ticket)</div>);
  }
  return wrap(<div className="text-xs" style={{ color: "#9ca3af" }}>Unknown block</div>);
}

// ---------------------------------------------------------------------------
// Empty state — shown when the canvas has no blocks
// ---------------------------------------------------------------------------

function EmptyEmail({ onOpenAI, onOpenTemplates, onScratch }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="canvas-empty-cta"
      style={{ color: "#374151" }}>
      <div className="h-14 w-14 rounded-full flex items-center justify-center mb-3"
        style={{ background: "#ecfeff", color: "#0e7490" }}>
        <Wand2 className="h-6 w-6" />
      </div>
      <div className="text-xl font-semibold" style={{ color: "#111827" }}>Start building your email</div>
      <div className="text-sm mt-1 opacity-80">Pick a starting point — you can always change your mind.</div>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        <button type="button" onClick={onOpenAI} data-testid="empty-start-ai"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium shadow">
          <Sparkles className="h-4 w-4" /> Start with AI
        </button>
        <button type="button" onClick={onOpenTemplates} data-testid="empty-start-template"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm"
          style={{ borderColor: "#d1d5db", color: "#111827", background: "#ffffff" }}>
          <LayoutTemplate className="h-4 w-4" /> Choose a template
        </button>
        <button type="button" onClick={onScratch} data-testid="empty-start-scratch"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm"
          style={{ color: "#111827", background: "transparent" }}>
          Start from scratch
        </button>
      </div>
      <div className="text-[11px] mt-4 opacity-60">or add content blocks from the left</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right rail — contextual settings panels
// ---------------------------------------------------------------------------

function SectionTitle({ children, testid }) {
  return <div className="text-[10px] font-semibold uppercase tracking-wider opacity-60 mb-1.5" data-testid={testid}>{children}</div>;
}

function ColorRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-2">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
          className="h-6 w-8 rounded cursor-pointer border border-white/10 bg-transparent"
          data-testid={testid} aria-label={label} />
        <code className="text-[10px] opacity-60">{value || ""}</code>
      </div>
    </div>
  );
}

function AlignRow({ value, onChange, testid, allowJustify = false }) {
  const opts = [
    { v: "left",    Icon: AlignLeft },
    { v: "center",  Icon: AlignCenter },
    { v: "right",   Icon: AlignRight },
  ];
  return (
    <div className="flex items-center gap-1 rounded-md border border-white/10 p-0.5" data-testid={testid} role="group" aria-label="Alignment">
      {opts.map(({ v, Icon }) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          data-testid={`${testid}-${v}`} aria-pressed={value === v} title={v}
          className={`flex-1 flex items-center justify-center rounded-sm px-2 py-1 text-xs
            ${value === v ? "bg-emerald-500/20 text-emerald-100" : "hover:bg-white/5 opacity-80"}`}>
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

function BlockSettingsPanel({ block, onChange, onDuplicate, onDelete, onOpenMediaPicker }) {
  // Refs at top so hook order is stable.
  const textRef = useRef(null);
  const btnLabelRef = useRef(null);
  const imgUrlRef = useRef(null);
  const altRef = useRef(null);
  if (!block) return null;
  const c = block.content || {}; const s = block.settings || {};
  const setC = (k, v) => onChange({ ...block, content: { ...c, [k]: v } });
  const setS = (k, v) => onChange({ ...block, settings: { ...s, [k]: v } });

  return (
    <div className="space-y-4" data-testid={`settings-${block.type}`}>
      <SectionTitle testid="settings-heading-content">Content</SectionTitle>

      {(block.type === "heading" || block.type === "text") && (
        <>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Text</Label>
            <MergeTagInserter inputRef={textRef} onInsert={(next) => setC("text", next)}
              testid={`settings-${block.type}-merge`} />
          </div>
          <Textarea rows={3} ref={textRef} value={c.text || ""} onChange={(e) => setC("text", e.target.value)}
            data-testid="settings-text" />
        </>
      )}

      {block.type === "image" && (
        <>
          {!c.src ? (
            <div className="space-y-2" data-testid="image-empty-settings">
              <div className="text-xs opacity-80">Add an image to this block.</div>
              <div className="flex flex-col gap-2">
                <Button size="sm" onClick={() => onOpenMediaPicker && onOpenMediaPicker(block.id)}
                  data-testid="settings-image-choose-media"
                  className="bg-emerald-500 hover:bg-emerald-400 text-white">
                  <ImageIcon className="h-4 w-4 mr-1" /> Choose from Media Library
                </Button>
                <div className="text-[11px] opacity-70 text-center">or</div>
                <div>
                  <Label className="text-xs">Use image URL</Label>
                  <Input ref={imgUrlRef} value={c.src || ""}
                    onChange={(e) => setC("src", e.target.value)}
                    placeholder="https://…" data-testid="settings-image-src" />
                  <div className="text-[10px] opacity-60 mt-1">HTTPS only. Unsafe schemes are blocked.</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded overflow-hidden border border-white/10" data-testid="settings-image-preview">
                <img src={c.src} alt={c.alt || ""} className="w-full h-32 object-cover" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  onClick={() => onOpenMediaPicker && onOpenMediaPicker(block.id)}
                  data-testid="settings-image-replace">
                  <ImageIcon className="h-3.5 w-3.5 mr-1" /> Replace image
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => setC("src", "")}
                  data-testid="settings-image-remove">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
              <div>
                <Label className="text-xs">Image URL</Label>
                <Input ref={imgUrlRef} value={c.src || ""}
                  onChange={(e) => setC("src", e.target.value)}
                  data-testid="settings-image-src" />
              </div>
            </div>
          )}
          <div className="mt-3">
            <Label className="text-xs">Alt text</Label>
            <Input ref={altRef} value={c.alt || ""} onChange={(e) => setC("alt", e.target.value)}
              placeholder="Describe this image…" data-testid="settings-image-alt" />
            <div className="text-[10px] opacity-70 mt-0.5" data-testid="settings-image-alt-helper">
              Describe this image for people who can't see it.
            </div>
          </div>
        </>
      )}

      {block.type === "button" && (
        <>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Label</Label>
            <MergeTagInserter inputRef={btnLabelRef} onInsert={(next) => setC("text", next)}
              testid="settings-button-merge" />
          </div>
          <Input ref={btnLabelRef} value={c.text || ""} onChange={(e) => setC("text", e.target.value)}
            data-testid="settings-button-text" />
          <Label className="text-xs mt-2">URL</Label>
          <Input value={c.url || ""} onChange={(e) => setC("url", e.target.value)}
            placeholder="https://…" data-testid="settings-button-url" />
          <div className="text-[10px] opacity-70 mt-1">Merge tags are not allowed in URLs (safety).</div>
        </>
      )}

      {block.type === "spacer" && (
        <>
          <Label className="text-xs">Height ({s.height || 24}px)</Label>
          <input type="range" min={4} max={96} value={s.height || 24}
            onChange={(e) => setS("height", Number(e.target.value))}
            data-testid="settings-spacer-height" className="w-full" />
        </>
      )}

      {/* STYLE section: only surfaces controls the renderer actually
          honours. Divider currently has no style controls in schema. */}
      {(block.type === "heading" || block.type === "text" || block.type === "button" || block.type === "image") && (
        <>
          <SectionTitle testid="settings-heading-style">Style</SectionTitle>
          {(block.type === "heading" || block.type === "text" || block.type === "button" || block.type === "image") && (
            <div>
              <Label className="text-xs">Align</Label>
              <AlignRow value={s.align || (block.type === "button" ? "center" : "left")}
                onChange={(v) => setS("align", v)}
                testid={`settings-align-${block.type}`} />
            </div>
          )}
          {block.type === "heading" && (
            <div>
              <Label className="text-xs">Size</Label>
              <div className="flex gap-1 mt-1" data-testid="settings-heading-size">
                {[1, 2, 3].map((lvl) => (
                  <button key={lvl} type="button" onClick={() => setS("level", lvl)}
                    data-testid={`settings-heading-size-${lvl}`}
                    aria-pressed={(s.level || 2) === lvl}
                    className={`flex-1 px-2 py-1 rounded text-xs border
                      ${(s.level || 2) === lvl ? "border-emerald-400 bg-emerald-500/20 text-emerald-100" : "border-white/10 hover:bg-white/5 opacity-80"}`}>
                    H{lvl}
                  </button>
                ))}
              </div>
            </div>
          )}
          {(block.type === "heading" || block.type === "text") && (
            <ColorRow label="Text color" value={s.color || (block.type === "heading" ? "#111827" : "#374151")}
              onChange={(v) => setS("color", v)}
              testid={`settings-color-${block.type}`} />
          )}
          {block.type === "button" && (
            <>
              <ColorRow label="Background" value={s.bg_color || "#111827"} onChange={(v) => setS("bg_color", v)}
                testid="settings-button-bg" />
              <ColorRow label="Text color" value={s.color || "#ffffff"} onChange={(v) => setS("color", v)}
                testid="settings-button-color" />
            </>
          )}
          {block.type === "image" && c.src && (
            <div>
              <Label className="text-xs">Max width ({s.max_width || 600}px)</Label>
              <input type="range" min={120} max={720} step={10}
                value={s.max_width || 600}
                onChange={(e) => setS("max_width", Number(e.target.value))}
                data-testid="settings-image-width" className="w-full" />
              <div className="text-[10px] opacity-60">Capped to the email's content width.</div>
            </div>
          )}
        </>
      )}

      <SectionTitle testid="settings-heading-actions">Actions</SectionTitle>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onDuplicate} data-testid="settings-action-duplicate">
          <Copy className="h-3.5 w-3.5 mr-1" /> Duplicate
        </Button>
        <Button variant="destructive" size="sm" onClick={onDelete} data-testid="settings-action-delete"
          className="ml-auto">
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}

function EmailDesignPanel({ theme, onTheme }) {
  const setT = (k, v) => onTheme({ ...theme, [k]: v });
  return (
    <div className="space-y-4" data-testid="email-design-panel">
      <SectionTitle testid="design-typography">Typography</SectionTitle>
      <div>
        <Label className="text-xs">Font family</Label>
        <select value={theme.font_family || FONT_CHOICES[0].value}
          onChange={(e) => setT("font_family", e.target.value)}
          data-testid="design-font-family"
          className="w-full bg-transparent border rounded px-2 py-1 text-sm mt-1 border-white/10">
          {FONT_CHOICES.map((f) => (
            <option key={f.value} value={f.value} style={{ color: "#111" }}>{f.label}</option>
          ))}
        </select>
        <div className="text-[10px] opacity-60 mt-1">Only email-safe families are listed.</div>
      </div>

      <SectionTitle testid="design-colors">Colors</SectionTitle>
      <ColorRow label="Email background" value={theme.bg_color || "#ffffff"}
        onChange={(v) => setT("bg_color", v)} testid="design-bg-color" />
      <ColorRow label="Body text color" value={theme.text_color || "#374151"}
        onChange={(v) => setT("text_color", v)} testid="design-text-color" />
      <ColorRow label="Accent (buttons)" value={theme.accent_color || "#111827"}
        onChange={(v) => setT("accent_color", v)} testid="design-accent-color" />

      <SectionTitle testid="design-width">Layout</SectionTitle>
      <div>
        <Label className="text-xs">Content width ({theme.max_width || 600}px)</Label>
        <input type="range" min={320} max={720} step={10} value={theme.max_width || 600}
          onChange={(e) => setT("max_width", Number(e.target.value))}
          data-testid="design-max-width" className="w-full" />
      </div>

      <div className="text-[10px] opacity-60 pt-2 border-t border-white/10">
        Design changes apply to the whole email and survive through to preview and send.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Media Library picker — reuses GET /api/media-library (workspace-scoped,
// authenticated) AND POST /api/media-library (same upload path as the
// main Media Library page). No new upload storage.
// Note on URLs: the existing Postmoro Media Library stores compressed
// images as `data:image/*;base64,...` payloads via canvas.toDataURL.
// The email renderer's image sanitizer now accepts both HTTPS and these
// data URLs (Ticket 2.7), so all Media Library items are selectable.
// ---------------------------------------------------------------------------

// Same accepted list as MediaLibrary.js so the upload paths agree.
const MEDIA_ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MEDIA_ACCEPTED_LABEL = "JPG, PNG or WEBP";

async function fileToCompressedDataUrl(file, maxDim = 1280, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const longest = Math.max(img.width, img.height);
        const scale = longest > maxDim ? maxDim / longest : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        try { resolve(canvas.toDataURL("image/jpeg", quality)); }
        catch (e) { reject(e); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function isEmailSafeMediaUrl(url) {
  const u = (url || "").trim();
  if (!u) return false;
  // Both delivery-safe options: HTTPS-hosted OR the base64 payloads
  // the existing Postmoro Media Library already produces. The email
  // renderer's image sanitizer accepts both.
  if (/^https:\/\//i.test(u)) return true;
  if (/^data:image\/(jpeg|png|gif|webp);base64,/i.test(u)) return true;
  return false;
}

function MediaLibraryPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  // Default OFF — the existing Postmoro Media Library stores images as
  // data URLs. Turning this on would hide every asset. Owners who want
  // to enforce hosted-URL delivery can flip it manually.
  const [safeOnly, setSafeOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get("/media-library");
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) { setErr("Could not load your Media Library."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected(null); setQuery(""); setUploadErr(null);
    load();
  }, [open, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items || []).filter((it) => {
      const url = (it.image_url || "").trim();
      if (!url) return false;
      // "Email-safe" filter accepts BOTH https AND embedded data-image
      // payloads — see isEmailSafeMediaUrl.
      if (safeOnly && !isEmailSafeMediaUrl(url)) return false;
      if (!q) return true;
      const blob = [it.description || "", (it.tags || []).join(" "), it.media_id || ""].join(" ").toLowerCase();
      return blob.includes(q);
    });
  }, [items, query, safeOnly]);

  const applyPick = (it) => {
    if (!it) return;
    onPick({ src: it.image_url, alt: (it.description || "").slice(0, 200) });
  };

  const doUpload = async (file) => {
    if (!file) return;
    if (!MEDIA_ACCEPTED_TYPES.has(file.type)) {
      setUploadErr(`Unsupported format. Please use ${MEDIA_ACCEPTED_LABEL}.`);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setUploadErr("That image is too large (max 8 MB).");
      return;
    }
    setUploadErr(null); setUploading(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      // Same POST path the main Media Library page uses.
      const { data: created } = await api.post("/media-library", { image_url: dataUrl });
      // Refresh + auto-select the new item.
      await load();
      if (created && created.media_id) setSelected(created.media_id);
      toast.success("Image added to your Media Library");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed. Please try again.";
      setUploadErr(typeof msg === "string" ? msg : "Upload failed.");
    } finally { setUploading(false); }
  };

  const onFileChange = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";  // reset so re-picking same file re-fires onChange
    await doUpload(f);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="media-library-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Choose from Media Library</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your media…" data-testid="media-search"
            className="flex-1 min-w-[220px]" />
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
            onChange={onFileChange} className="hidden" data-testid="media-upload-input" />
          <Button size="sm" onClick={() => fileRef.current && fileRef.current.click()}
            disabled={uploading} data-testid="media-upload-button"
            className="bg-emerald-500 hover:bg-emerald-400 text-white">
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Uploading…</> : <><Plus className="h-4 w-4 mr-1" /> Upload image</>}
          </Button>
          <label className="flex items-center gap-2 text-xs opacity-80 select-none">
            <input type="checkbox" checked={safeOnly} onChange={(e) => setSafeOnly(e.target.checked)}
              data-testid="media-safe-only" />
            Only email-safe images
          </label>
        </div>
        {uploadErr ? (
          <div className="text-xs text-red-400" data-testid="media-upload-error">{uploadErr}</div>
        ) : null}

        {loading ? (
          <div className="py-10 text-center text-sm opacity-70" data-testid="media-loading">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading your Media Library…
          </div>
        ) : err ? (
          <div className="py-10 text-center text-sm text-red-400" data-testid="media-error">{err}</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="media-empty">
            <ImageIcon className="h-8 w-8 mx-auto opacity-60 mb-2" />
            <div className="text-sm">
              {items.length === 0
                ? "No images in your Media Library yet."
                : "No images match your search."}
            </div>
            {items.length === 0 ? (
              <div className="mt-3">
                <Button size="sm" onClick={() => fileRef.current && fileRef.current.click()}
                  disabled={uploading} data-testid="media-upload-empty"
                  className="bg-emerald-500 hover:bg-emerald-400 text-white">
                  <Plus className="h-4 w-4 mr-1" /> Upload image
                </Button>
                <div className="text-[11px] opacity-70 mt-2">or use the Media page to add images, then come back here.</div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="media-grid">
            {filtered.map((it) => (
              <button key={it.media_id} type="button"
                onClick={() => setSelected(it.media_id)}
                onDoubleClick={() => applyPick(it)}
                data-testid={`media-item-${it.media_id}`}
                aria-pressed={selected === it.media_id}
                className={`group rounded-md overflow-hidden text-left border transition
                  ${selected === it.media_id
                    ? "border-emerald-400 ring-2 ring-emerald-400/60"
                    : "border-white/10 hover:border-emerald-400/60"}`}>
                <div className="aspect-square bg-white/5 flex items-center justify-center">
                  <img src={it.image_url} alt={it.description || "media"}
                    loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-1.5">
                  <div className="text-[11px] truncate opacity-90">{it.description || it.media_id}</div>
                  <div className="text-[9px] opacity-60 flex items-center justify-between">
                    <span>{/^https:\/\//i.test(it.image_url) ? "https" : (/^data:image/i.test(it.image_url) ? "upload" : "other")}</span>
                    {it.is_favorite ? <span>★</span> : null}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between">
          <div className="text-[11px] opacity-60">
            Images are workspace-scoped — you only see media from the current business.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="media-cancel">Cancel</Button>
            <Button onClick={() => applyPick(filtered.find((x) => x.media_id === selected))}
              disabled={!selected} data-testid="media-select">Use image</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Template picker with mini rendered previews
// ---------------------------------------------------------------------------

function MiniTemplatePreview({ schema }) {
  const blocks = (schema && schema.blocks) || [];
  return (
    <div className="rounded border overflow-hidden bg-white" style={{ borderColor: "#e5e7eb" }}>
      <div className="p-2 space-y-1 min-h-[120px]">
        {blocks.slice(0, 6).map((b, i) => {
          if (b.type === "heading") {
            return <div key={i} className="font-semibold" style={{ color: "#111827", fontSize: 12 }}>{(b.content?.text || "Heading").slice(0, 40)}</div>;
          }
          if (b.type === "text") {
            return <div key={i} style={{ color: "#374151", fontSize: 10 }}>{(b.content?.text || "").slice(0, 90)}</div>;
          }
          if (b.type === "image") {
            return <div key={i} className="rounded" style={{ background: "#e5e7eb", height: 22 }} />;
          }
          if (b.type === "button") {
            return <div key={i} className="text-center"><span className="inline-block rounded" style={{ background: "#111827", color: "#fff", fontSize: 9, padding: "2px 8px" }}>{(b.content?.text || "Learn more").slice(0, 20)}</span></div>;
          }
          if (b.type === "divider") return <hr key={i} style={{ borderTop: "1px solid #e5e7eb" }} />;
          if (b.type === "spacer")  return <div key={i} style={{ height: 6 }} />;
          return null;
        })}
        {blocks.length === 0 && <div className="text-[10px] italic text-center py-6" style={{ color: "#9ca3af" }}>Blank canvas</div>}
      </div>
    </div>
  );
}

function TemplatePickerDialog({ open, onClose, onApply }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/email/templates");
        setItems(data.templates || []);
      } catch { toast.error("Failed to load templates"); }
      finally { setLoading(false); }
    })();
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="template-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LayoutTemplate className="h-4 w-4" /> Choose a starter template</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm opacity-70 py-8 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {items.map((t) => (
              <button key={t.template_id} onClick={() => onApply(t)}
                data-testid={`template-${t.template_id}`}
                className="text-left border border-white/15 rounded-md p-2 hover:border-emerald-400/60 hover:bg-white/5 transition space-y-2">
                <MiniTemplatePreview schema={t.schema} />
                <div>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-xs opacity-70 line-clamp-2">{t.description}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// AI Draft Dialog — same behavior, minor visual tightening
// ---------------------------------------------------------------------------

function Chips({ value, options, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-chips`}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            data-testid={`${testidPrefix}-chip-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`text-xs px-2.5 py-1 rounded-full border transition ${active
              ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-100"
              : "border-white/15 hover:bg-white/5 opacity-90"}`}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function AIDraftDialog({ open, onClose, onApply }) {
  const [goal, setGoal] = useState("");
  const [audience, setAudience] = useState("All subscribers");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("Match my brand");
  const [ctaChoice, setCtaChoice] = useState("");
  const [ctaCustom, setCtaCustom] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [offer, setOffer] = useState("");
  const [deadline, setDeadline] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [destination, setDestination] = useState("");
  const [important, setImportant] = useState("");
  const [instructions, setInstructions] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setResult(null); setErrors({}); setBusy(false); }
  }, [open]);

  const cta = ctaChoice === "Custom" ? ctaCustom : ctaChoice;

  const submit = async () => {
    const next = {};
    if (!goal.trim()) next.goal = "Tell us what you want this campaign to accomplish.";
    if (!topic.trim()) next.topic = "Add a few details about what you're promoting so the AI has enough context.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    const extraLines = [];
    if (offer)        extraLines.push(`Offer/discount: ${offer}`);
    if (deadline)     extraLines.push(`Deadline: ${deadline}`);
    if (promoCode)    extraLines.push(`Promo code: ${promoCode}`);
    if (destination)  extraLines.push(`Destination URL: ${destination}`);
    if (important)    extraLines.push(`Important details: ${important}`);
    if (instructions) extraLines.push(`Additional instructions: ${instructions}`);
    const notesBundled = extraLines.join("\n");
    try {
      const { data } = await api.post("/email/builder/ai-draft", {
        goal, audience, topic,
        tone: tone === "Match my brand" ? "" : tone,
        cta, notes: notesBundled,
      });
      setResult(data);
    } catch { toast.error("AI draft failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-draft-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="h-4 w-4" /> Create with AI</DialogTitle>
        </DialogHeader>
        <div className="text-sm opacity-80 -mt-2">
          Tell Postmoro what you want to promote and we'll build a complete email using your business profile and brand voice.
        </div>

        <div className="space-y-1.5" data-testid="ai-section-goal">
          <Label className="text-xs font-medium">Campaign goal</Label>
          <div className="text-xs opacity-60">What do you want this email to accomplish?</div>
          <Chips value={goal} options={AI_GOAL_CHOICES} onChange={setGoal} testidPrefix="ai-goal" />
          <Input value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="ai-input-goal"
            placeholder="Example: Promote our 50% off Labor Day sale and drive purchases" />
          {errors.goal ? <div className="text-xs text-red-400" data-testid="ai-error-goal">{errors.goal}</div> : null}
        </div>

        <div className="space-y-1.5" data-testid="ai-section-audience">
          <Label className="text-xs font-medium">Audience</Label>
          <div className="text-xs opacity-60">Who should this email speak to?</div>
          <Chips value={audience} options={AI_AUDIENCE_CHOICES} onChange={setAudience} testidPrefix="ai-audience" />
          <Input value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="ai-input-audience" />
        </div>

        <div className="space-y-1.5" data-testid="ai-section-topic">
          <Label className="text-xs font-medium">What are you promoting?</Label>
          <div className="text-xs opacity-60">Describe the product, service, offer, announcement, or message.</div>
          <Textarea rows={4} value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="ai-input-topic" />
          {errors.topic ? <div className="text-xs text-red-400" data-testid="ai-error-topic">{errors.topic}</div> : null}
        </div>

        <div className="space-y-1.5" data-testid="ai-section-tone">
          <Label className="text-xs font-medium">Tone</Label>
          <Chips value={tone} options={AI_TONE_CHOICES} onChange={setTone} testidPrefix="ai-tone" />
        </div>

        <div className="space-y-1.5" data-testid="ai-section-cta">
          <Label className="text-xs font-medium">Call to action</Label>
          <div className="text-xs opacity-60">What do you want readers to do?</div>
          <Chips value={ctaChoice} options={[...AI_CTA_CHOICES, "Custom"]} onChange={setCtaChoice} testidPrefix="ai-cta" />
          {ctaChoice === "Custom" ? (
            <Input value={ctaCustom} onChange={(e) => setCtaCustom(e.target.value)} data-testid="ai-cta-custom" />
          ) : null}
        </div>

        <div>
          <button type="button" onClick={() => setAdvOpen(!advOpen)}
            className="text-xs underline opacity-80" data-testid="ai-toggle-advanced">
            {advOpen ? "Hide" : "Add more details"}
          </button>
          {advOpen ? (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ai-advanced">
              <div><Label className="text-xs">Offer / discount</Label>
                <Input value={offer} onChange={(e) => setOffer(e.target.value)} data-testid="ai-adv-offer" /></div>
              <div><Label className="text-xs">Deadline</Label>
                <Input value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="ai-adv-deadline" /></div>
              <div><Label className="text-xs">Promo code</Label>
                <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value)} data-testid="ai-adv-code" /></div>
              <div><Label className="text-xs">Destination / link</Label>
                <Input value={destination} onChange={(e) => setDestination(e.target.value)} data-testid="ai-adv-url" /></div>
              <div className="md:col-span-2"><Label className="text-xs">Important details</Label>
                <Textarea rows={2} value={important} onChange={(e) => setImportant(e.target.value)} data-testid="ai-adv-important" /></div>
            </div>
          ) : null}
        </div>

        <div className="space-y-1" data-testid="ai-section-instructions">
          <Label className="text-xs font-medium">Additional instructions</Label>
          <div className="text-xs opacity-60">Anything else you want Postmoro to know?</div>
          <Textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="ai-input-notes" />
        </div>

        <div className="rounded-md border border-white/10 p-3 text-xs opacity-90 space-y-1" data-testid="ai-explainer">
          <div className="font-medium">Postmoro will create:</div>
          <div>✓ 3 subject line options</div>
          <div>✓ Preheader</div>
          <div>✓ Email headline</div>
          <div>✓ Email body copy</div>
          <div>✓ Call-to-action button</div>
          <div>✓ Complete editable email layout</div>
        </div>

        {result ? (
          <div className="mt-1 space-y-2 border-t border-white/10 pt-3" data-testid="ai-result">
            {result.brand_context && (result.brand_context.business_name_present || result.brand_context.brand_voice_present) ? (
              <div className="text-xs opacity-70 flex items-center gap-1" data-testid="brand-context-indicator">
                <Sparkles className="h-3 w-3" /> Using your Business Profile{result.brand_context.brand_voice_present ? " + Brand Learning" : ""}
              </div>
            ) : null}
            <div className="text-xs opacity-70">Subject ideas — click to preview in the editor:</div>
            <div className="space-y-1">
              {(result.subject_suggestions || []).map((s, i) => (
                <button key={i} data-testid={`ai-subject-${i}`}
                  onClick={() => onApply({ ...result, subject_pick: s })}
                  className="w-full text-left px-3 py-2 rounded border border-white/15 hover:bg-white/5 text-sm">{s}</button>
              ))}
            </div>
          </div>
        ) : null}

        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} data-testid="ai-cancel">Cancel</Button>
          {result ? (
            <>
              <Button variant="outline" onClick={() => setResult(null)} data-testid="ai-regenerate">Edit inputs</Button>
              <Button variant="outline" onClick={submit} disabled={busy} data-testid="ai-regen">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Regenerate
              </Button>
              <Button onClick={() => onApply(result)} data-testid="ai-apply">Apply draft</Button>
            </>
          ) : (
            <Button onClick={submit} disabled={busy} data-testid="ai-generate">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Creating your email…</> : "Generate"}
            </Button>
          )}
        </DialogFooter>
        {busy ? <div className="text-xs opacity-60 text-center" data-testid="ai-loading-note">Using your brand voice and campaign details</div> : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Preview modal (personalized preview kept from Ticket 2)
// ---------------------------------------------------------------------------

function PreviewDialog({ open, onClose, html, personalized, sampleData, onTogglePersonalize, onSampleChange }) {
  const [mode, setMode] = useState("desktop");
  const width = mode === "mobile" ? 375 : 600;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4" /> Preview
            <div className="ml-4 flex gap-1">
              <Button size="sm" variant={mode === "desktop" ? "default" : "outline"} onClick={() => setMode("desktop")} data-testid="preview-desktop">Desktop</Button>
              <Button size="sm" variant={mode === "mobile" ? "default" : "outline"} onClick={() => setMode("mobile")} data-testid="preview-mobile">Mobile</Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between gap-3 text-xs border border-white/10 rounded-md p-2" data-testid="preview-personalize-row">
          <div className="flex items-center gap-2">
            <input id="pv-personalize" type="checkbox" checked={!!personalized}
              onChange={(e) => onTogglePersonalize(e.target.checked)}
              data-testid="preview-personalize-toggle" />
            <label htmlFor="pv-personalize" className="cursor-pointer">Preview with sample recipient</label>
            {personalized ? <Badge variant="secondary" data-testid="preview-personalize-badge">Sample data — not real contact</Badge> : null}
          </div>
          {personalized ? (
            <div className="text-[10px] opacity-70">business_name is always read from your current workspace.</div>
          ) : null}
        </div>
        {personalized ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs" data-testid="preview-sample-form">
            {["first_name", "last_name", "email"].map((k) => (
              <div key={k}>
                <Label className="text-[10px] opacity-70">{k}</Label>
                <Input value={sampleData?.[k] || ""} onChange={(e) => onSampleChange({ ...sampleData, [k]: e.target.value })}
                  data-testid={`preview-sample-${k}`} />
              </div>
            ))}
          </div>
        ) : null}
        <div className="flex justify-center bg-neutral-100 p-4 rounded max-h-[70vh] overflow-auto">
          <iframe title="preview" sandbox="" srcDoc={html || ""} style={{ width, minHeight: 400, background: "#fff", border: "0" }} data-testid="preview-iframe" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

const DEFAULT_THEME = {
  max_width: 600,
  font_family: "Helvetica, Arial, sans-serif",
  bg_color: "#f3f4f6",
  text_color: "#374151",
  accent_color: "#111827",
};

export default function EmailBuilder({ campaignId, onSaved, isOwnerDev }) {
  const [name, setName] = useState("Untitled campaign");
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [templateId, setTemplateId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [cid, setCid] = useState(campaignId || null);
  const [readOnly, setReadOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewPersonalized, setPreviewPersonalized] = useState(false);
  const [previewSample, setPreviewSample] = useState({ first_name: "Sarah", last_name: "Johnson", email: "sarah@example.com" });
  const [device, setDevice] = useState("desktop");
  const [zoom, setZoom] = useState(1.0);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerTargetId, setMediaPickerTargetId] = useState(null);
  const subjectRef = useRef(null);
  const preheaderRef = useRef(null);

  const selected = useMemo(() => blocks.find((b) => b.id === selectedId), [blocks, selectedId]);
  const markDirty = () => setDirty(true);

  useEffect(() => {
    if (!campaignId || !isOwnerDev) return;
    (async () => {
      try {
        const { data } = await api.get(`/email/campaigns/${campaignId}`);
        setName(data.name || "Untitled campaign");
        setSubject(data.subject || "");
        setPreheader(data.preview_text || "");
        setTemplateId(data.template_id || null);
        setCid(data.campaign_id || data.id);
        if (["sending", "sent", "completed"].includes(data.status)) setReadOnly(true);
        const schema = data.builder_schema;
        if (schema && Array.isArray(schema.blocks)) {
          setBlocks(schema.blocks.map((b, i) => ({ ...b, id: b.id || uid(), order: i })));
          if (schema.theme) setTheme({ ...DEFAULT_THEME, ...schema.theme });
        } else if (data.body_html) {
          setReadOnly(true);
          toast.info("Legacy raw-HTML campaign — read-only in the new builder");
        }
      } catch { toast.error("Failed to load draft"); }
    })();
  }, [campaignId, isOwnerDev]);

  const addBlock = useCallback((tmpl, atIndex = null) => {
    if (readOnly) return;
    const nb = { id: uid(), type: tmpl.type, ...tmpl.seed };
    setBlocks((cur) => {
      const arr = [...cur];
      const idx = atIndex === null ? arr.length : atIndex;
      arr.splice(idx, 0, nb);
      return arr.map((b, i) => ({ ...b, order: i }));
    });
    setSelectedId(nb.id);
    markDirty();
  }, [readOnly]);

  const updateBlock = useCallback((next) => {
    setBlocks((cur) => cur.map((b) => (b.id === next.id ? next : b)));
    markDirty();
  }, []);

  const moveBlock = useCallback((id, dir) => {
    setBlocks((cur) => {
      const idx = cur.findIndex((b) => b.id === id); if (idx < 0) return cur;
      const to = dir === "up" ? idx - 1 : idx + 1;
      if (to < 0 || to >= cur.length) return cur;
      const arr = [...cur]; const [it] = arr.splice(idx, 1); arr.splice(to, 0, it);
      return arr.map((b, i) => ({ ...b, order: i }));
    });
    markDirty();
  }, []);

  const duplicateBlock = useCallback((id) => {
    setBlocks((cur) => {
      const idx = cur.findIndex((b) => b.id === id); if (idx < 0) return cur;
      const clone = { ...cur[idx], id: uid() };
      const arr = [...cur]; arr.splice(idx + 1, 0, clone);
      return arr.map((b, i) => ({ ...b, order: i }));
    });
    markDirty();
  }, []);

  const deleteBlock = useCallback((id) => {
    setBlocks((cur) => cur.filter((b) => b.id !== id).map((b, i) => ({ ...b, order: i })));
    if (selectedId === id) setSelectedId(null);
    markDirty();
  }, [selectedId]);

  const applyTemplate = (t) => {
    const proceed = !dirty || window.confirm("Replace current content with this template? Unsaved changes will be lost.");
    if (!proceed) return;
    const schema = t.schema || {};
    const nb = (schema.blocks || []).map((b, i) => ({ ...b, id: uid(), order: i }));
    setBlocks(nb); setTemplateId(t.template_id); setSelectedId(null);
    if (schema.theme) setTheme({ ...DEFAULT_THEME, ...schema.theme });
    setDirty(true); setTplOpen(false);
    toast.success(`Template "${t.name}" applied`);
  };

  const applyAI = (r) => {
    const proceed = !dirty || window.confirm("Replace current content with the AI draft? Unsaved changes will be lost.");
    if (!proceed) return;
    const nb = (r.builder_schema?.blocks || []).map((b, i) => ({ ...b, id: uid(), order: i }));
    setBlocks(nb);
    setSubject(r.subject_pick || (r.subject_suggestions?.[0] || subject));
    setPreheader(r.preheader || preheader);
    if (r.builder_schema?.theme) setTheme({ ...DEFAULT_THEME, ...r.builder_schema.theme });
    setSelectedId(null); setDirty(true); setAiOpen(false);
    toast.success("AI draft applied");
  };

  const runPreview = async (options = {}) => {
    const usePersonalize = options.personalize ?? previewPersonalized;
    try {
      const body = {
        subject, preheader,
        builder_schema: { theme, blocks: blocks.map((b, i) => ({ ...b, order: i })) },
      };
      if (usePersonalize) { body.use_sample = true; body.sample_data = previewSample; }
      const { data } = await api.post("/email/builder/preview", body);
      setPreviewHtml(data.html);
      setPreviewPersonalized(!!data.personalized);
      setPreviewOpen(true);
    } catch { toast.error("Preview failed"); }
  };
  const togglePreviewPersonalize = async (next) => { setPreviewPersonalized(next); await runPreview({ personalize: next }); };
  const updatePreviewSample = async (next) => {
    setPreviewSample(next);
    if (previewPersonalized) setTimeout(() => runPreview({ personalize: true }), 200);
  };

  const save = async () => {
    if (readOnly) { toast.warning("This campaign is not editable"); return; }
    setBusy(true);
    try {
      const payload = {
        campaign_id: cid || undefined, name, subject, preheader,
        template_id: templateId || undefined,
        builder_schema: { theme, blocks: blocks.map((b, i) => ({ ...b, order: i })) },
      };
      const { data } = await api.post("/email/builder/save", payload);
      setCid(data.campaign_id || data.id);
      setDirty(false);
      toast.success(cid ? "Draft updated" : "Draft saved");
      if (onSaved) onSaved(data);
    } catch (e) {
      if (e?.response?.status === 409) {
        setReadOnly(true);
        toast.error("This campaign has already been sent — clone it to edit.");
      } else { toast.error("Save failed"); }
    } finally { setBusy(false); }
  };

  if (!isOwnerDev) return null;

  const canvasWidth = device === "mobile" ? 375 : (theme.max_width || 600);

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-3" data-testid="email-builder">
      {/* -------------------- Top toolbar -------------------- */}
      <GlassCard className="p-4" data-testid="builder-topbar">
        <div className="flex flex-col md:flex-row md:items-start gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <div>
              <Label htmlFor="builder-name-input" className="text-[11px] uppercase tracking-wide opacity-70">Campaign name</Label>
              <Input id="builder-name-input" value={name}
                onChange={(e) => { setName(e.target.value); markDirty(); }}
                className="text-lg font-semibold mt-1" data-testid="builder-name" />
              <div className="text-[10px] opacity-60 mt-0.5" data-testid="builder-name-helper">Only you will see this.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] uppercase tracking-wide opacity-70">Subject <span className="opacity-60 normal-case">({subject.length}/180)</span></Label>
                  <MergeTagInserter inputRef={subjectRef} onInsert={(next) => { setSubject(next.slice(0, 180)); markDirty(); }} testid="subject-merge" />
                </div>
                <Input ref={subjectRef} value={subject}
                  onChange={(e) => { setSubject(e.target.value.slice(0, 180)); markDirty(); }}
                  data-testid="builder-subject" />
                <div className="text-[10px] opacity-60 mt-0.5" data-testid="builder-subject-helper">Recipients will see this.</div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] uppercase tracking-wide opacity-70">Preheader <span className="opacity-60 normal-case">({preheader.length}/200)</span></Label>
                  <MergeTagInserter inputRef={preheaderRef} onInsert={(next) => { setPreheader(next.slice(0, 200)); markDirty(); }} testid="preheader-merge" />
                </div>
                <Input ref={preheaderRef} value={preheader}
                  onChange={(e) => { setPreheader(e.target.value.slice(0, 200)); markDirty(); }}
                  data-testid="builder-preheader" />
                <div className="text-[10px] opacity-60 mt-0.5" data-testid="builder-preheader-helper">Preview text shown beside the subject in many inboxes.</div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:flex-col md:items-stretch md:min-w-[190px]" data-testid="builder-toolbar-actions">
            <Button variant="outline" size="sm" onClick={() => setTplOpen(true)} data-testid="btn-templates">
              <LayoutTemplate className="h-4 w-4 mr-1" /> Templates
            </Button>
            <Button size="sm" onClick={() => setAiOpen(true)} data-testid="btn-ai-draft"
              className="bg-emerald-500 hover:bg-emerald-400 text-white">
              <Sparkles className="h-4 w-4 mr-1" /> Create with AI
            </Button>
            <Button variant="outline" size="sm" onClick={runPreview} data-testid="btn-preview">
              <Eye className="h-4 w-4 mr-1" /> Preview
            </Button>
            <Button size="sm" onClick={save} disabled={busy || readOnly} data-testid="btn-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              {cid ? "Update draft" : "Save draft"}
            </Button>
          </div>
        </div>
        {readOnly ? (
          <div className="mt-2 text-xs text-amber-400" data-testid="builder-readonly-note">
            Read-only — this campaign is already sent or is a legacy raw-HTML campaign.
          </div>
        ) : null}
        {dirty ? <div className="mt-1 text-xs opacity-60" data-testid="builder-dirty">Unsaved changes</div> : null}
      </GlassCard>

      {/* -------------------- Body layout: left / center / right -------------------- */}
      <div className="grid grid-cols-12 gap-3">
        {/* Left rail */}
        <GlassCard className="p-3 col-span-12 md:col-span-2 min-h-[400px]" data-testid="builder-left-rail">
          <BlockPalette onAdd={(t) => addBlock(t)} />
        </GlassCard>

        {/* Center: real email canvas */}
        <div className="col-span-12 md:col-span-7 rounded-xl overflow-hidden"
             style={{ background: "#e5e7eb" }}
             data-testid="canvas-outer" data-builder-canvas-outer>
          {/* Canvas toolbar: device toggle + zoom */}
          <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "#d1d5db" }}>
            <div className="flex items-center gap-1" data-testid="canvas-device-toggle" role="group" aria-label="Device preview">
              <Tooltip><TooltipTrigger asChild>
                <button type="button" onClick={() => setDevice("desktop")}
                  aria-pressed={device === "desktop"} aria-label="Desktop preview"
                  data-testid="device-desktop"
                  className={`px-2 py-1 rounded-md text-xs inline-flex items-center gap-1
                    ${device === "desktop" ? "bg-emerald-500 text-white" : "hover:bg-white/60 text-neutral-700"}`}>
                  <Monitor className="h-3.5 w-3.5" /> Desktop
                </button>
              </TooltipTrigger><TooltipContent>Desktop preview</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild>
                <button type="button" onClick={() => setDevice("mobile")}
                  aria-pressed={device === "mobile"} aria-label="Mobile preview"
                  data-testid="device-mobile"
                  className={`px-2 py-1 rounded-md text-xs inline-flex items-center gap-1
                    ${device === "mobile" ? "bg-emerald-500 text-white" : "hover:bg-white/60 text-neutral-700"}`}>
                  <Smartphone className="h-3.5 w-3.5" /> Mobile
                </button>
              </TooltipTrigger><TooltipContent>Mobile preview</TooltipContent></Tooltip>
            </div>
            <div className="flex items-center gap-1 text-neutral-700" data-testid="canvas-zoom" role="group" aria-label="Editor zoom">
              <button type="button" onClick={() => setZoom(ZOOM_STEPS[Math.max(0, ZOOM_STEPS.indexOf(zoom) - 1)] || ZOOM_STEPS[0])}
                aria-label="Zoom out" data-testid="zoom-out"
                className="p-1 rounded hover:bg-white/60"><ZoomOut className="h-3.5 w-3.5" /></button>
              <span className="text-xs w-10 text-center tabular-nums" data-testid="zoom-value">{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, ZOOM_STEPS.indexOf(zoom) + 1)] || ZOOM_STEPS[ZOOM_STEPS.length - 1])}
                aria-label="Zoom in" data-testid="zoom-in"
                className="p-1 rounded hover:bg-white/60"><ZoomIn className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          <div className="overflow-auto min-h-[520px] max-h-[75vh] p-6 flex justify-center"
               style={{ background: "#e5e7eb", colorScheme: "light" }}
               data-testid="builder-canvas">
            <div
              style={{
                width: canvasWidth,
                backgroundColor: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,.08), 0 8px 24px rgba(0,0,0,.06)",
                borderRadius: 6,
                padding: 24,
                color: theme.text_color || "#374151",
                fontFamily: theme.font_family || "Helvetica, Arial, sans-serif",
                transform: `scale(${zoom})`,
                transformOrigin: "top center",
              }}
              data-testid="email-canvas"
              data-builder-canvas
              onClick={() => setSelectedId(null)}
            >
              {blocks.length === 0 ? (
                <div data-testid="canvas-empty">
                <EmptyEmail
                  onOpenAI={() => setAiOpen(true)}
                  onOpenTemplates={() => setTplOpen(true)}
                  onScratch={() => addBlock(BLOCK_LIBRARY[0])}
                />
                </div>
              ) : (
                <div onClick={(e) => e.stopPropagation()}>
                  <InsertBetween testid="insert-0" onAdd={() => addBlock(BLOCK_LIBRARY[1], 0)} />
                  {blocks.map((b, i) => (
                    <React.Fragment key={b.id}>
                      <BlockView block={b}
                        selected={selectedId === b.id}
                        hovered={hoveredId === b.id}
                        onSelect={setSelectedId}
                        onHover={setHoveredId}
                        onLeave={() => setHoveredId(null)}
                        onChange={updateBlock}
                        isFirst={i === 0} isLast={i === blocks.length - 1}
                        onMoveUp={() => moveBlock(b.id, "up")}
                        onMoveDown={() => moveBlock(b.id, "down")}
                        onDuplicate={() => duplicateBlock(b.id)}
                        onDelete={() => deleteBlock(b.id)}
                      />
                      <InsertBetween testid={`insert-${i + 1}`} onAdd={() => addBlock(BLOCK_LIBRARY[1], i + 1)} />
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right rail */}
        <GlassCard className="p-3 col-span-12 md:col-span-3 min-h-[400px]" data-testid="builder-settings-panel">
          <div data-testid="builder-right-rail">
          {selected ? (
            <BlockSettingsPanel block={selected} onChange={updateBlock}
              onDuplicate={() => duplicateBlock(selected.id)}
              onDelete={() => deleteBlock(selected.id)}
              onOpenMediaPicker={(bid) => { setMediaPickerTargetId(bid); setMediaPickerOpen(true); }} />
          ) : (
            <EmailDesignPanel theme={theme} onTheme={(t) => { setTheme(t); markDirty(); }} />
          )}
          </div>
        </GlassCard>
      </div>

      <TemplatePickerDialog open={tplOpen} onClose={() => setTplOpen(false)} onApply={applyTemplate} />
      <AIDraftDialog open={aiOpen} onClose={() => setAiOpen(false)} onApply={applyAI} />
      <PreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)}
        html={previewHtml}
        personalized={previewPersonalized}
        sampleData={previewSample}
        onTogglePersonalize={togglePreviewPersonalize}
        onSampleChange={updatePreviewSample}
      />
      <MediaLibraryPicker open={mediaPickerOpen} onClose={() => setMediaPickerOpen(false)}
        onPick={({ src, alt }) => {
          const bid = mediaPickerTargetId;
          if (!bid) { setMediaPickerOpen(false); return; }
          setBlocks((cur) => cur.map((b) => {
            if (b.id !== bid) return b;
            const c = b.content || {};
            // Preserve existing alt if the user already typed one.
            const nextAlt = (c.alt && c.alt.trim()) ? c.alt : (alt || "");
            return { ...b, content: { ...c, src, alt: nextAlt } };
          }));
          setSelectedId(bid);
          setMediaPickerOpen(false);
          markDirty();
          toast.success("Image added to your email");
        }} />
    </div>
    </TooltipProvider>
  );
}
